/* ============================================================================
   haiku-transition — cover the exit so navigation stops flashing

   WHAT WAS WRONG
   There is no transition layer at all. Every navigation is a full document
   load: the browser tears down the current page, paints the UA's default
   white for a frame or two, then the new document arrives. That white gap is
   the flicker; the jump is content settling as the new page paints.

   WHAT THIS DOES
   Only the EXIT side, deliberately. On a plain left-click of a same-origin
   link we hold the navigation for one short fade, raise a veil painted in the
   page's own ground colour, and only then let the browser go. The next
   document's first paint is that same ground, so the two read as one
   continuous surface and the white frame never happens. (Until 2026-10-21 the
   first page of a visit opened under haiku-loader; it was removed.)

   NOT an SPA. Nothing is intercepted, re-rendered or routed; the browser still
   performs a normal navigation. That keeps Shopify's own behaviour, history,
   caching and analytics intact, and means the failure mode is "no animation"
   rather than "site broken".

   FAIL-SAFES — a veil that sticks is worse than a flicker, so:
     · a hard timer removes it if navigation has not happened in TIMEOUT ms
     · pageshow (including bfcache restores, where the DOM comes back exactly
       as it left with the veil still up) always clears it
     · reduced-motion navigates immediately with no veil at all
     · any throw falls through to normal navigation
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuTransitionConfig || {};
  if (cfg.enabled === false) return;

  var OUT = typeof cfg.out === 'number' ? cfg.out : 260;
  var TIMEOUT = typeof cfg.timeout === 'number' ? cfg.timeout : 2500;

  var root = document.documentElement;
  var veil = null;
  var going = false;
  var timer = 0;

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function build() {
    if (veil) return veil;
    veil = document.createElement('div');
    veil.className = 'hx-veil';
    veil.setAttribute('aria-hidden', 'true');
    document.body.appendChild(veil);
    return veil;
  }

  function clear() {
    going = false;
    if (timer) { window.clearTimeout(timer); timer = 0; }
    root.classList.remove('hx-out');
    if (veil && veil.parentNode) { veil.parentNode.removeChild(veil); veil = null; }
  }

  /* Which clicks are ours. Everything not obviously a same-document, same-tab,
     plain navigation is left completely alone — modified clicks, new tabs,
     downloads, anchors, other origins, and anything a script already handled. */
  function navigable(e, a) {
    if (e.defaultPrevented) return false;
    if (e.button !== 0) return false;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
    if (!a || !a.href) return false;
    if (a.target && a.target !== '_self') return false;
    if (a.hasAttribute('download')) return false;
    if (a.getAttribute('rel') === 'external') return false;
    if (a.hasAttribute('data-hx-skip')) return false;

    /* Anything that opens IN PLACE must be left alone. The collection tiles
       are <a href="/products/...">, so they look like ordinary navigation —
       but the gallery cancels the click and opens the preview instead. This
       layer was hijacking them and sending the visitor to the product page,
       which is the regression this list prevents.

       Belt and braces with the bubble-phase move below: bubbling means the
       gallery's own preventDefault has already run and `defaultPrevented`
       catches it. This list also covers handlers that open in place WITHOUT
       calling preventDefault. */
    if (a.closest(
      '.gallery__slide, .gallery__link, .hg, .content, .hp-nav, .hp-half, ' +
      '.hi-lb, .hi-lb-open, [data-pe-frame], [data-hi-lb-stage]'
    )) return false;

    var href = a.getAttribute('href') || '';
    if (href.charAt(0) === '#') return false;
    if (/^(mailto:|tel:|javascript:)/i.test(href)) return false;

    var url;
    try { url = new URL(a.href, window.location.href); } catch (err) { return false; }
    if (url.origin !== window.location.origin) return false;

    // Same page, different hash — the browser scrolls, nothing unloads.
    if (url.pathname === window.location.pathname &&
        url.search === window.location.search &&
        url.hash) return false;

    return url;
  }

  /* BUBBLE phase, deliberately. In capture this ran BEFORE every other click
     handler on the page, so preventDefault() here beat the gallery's own
     handler and turned an in-place preview into a full navigation. Bubbling
     means anything that wants the click has already had it, and
     e.defaultPrevented below tells us so. The cost is that a handler calling
     stopPropagation() hides the click from us — that degrades to a plain
     navigation with no veil, which is the right way to fail. */
  document.addEventListener('click', function (e) {
    try {
      if (going) return;
      var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
      if (!a) return;
      var url = navigable(e, a);
      if (!url) return;
      if (reduced()) return;   // straight through, no veil

      e.preventDefault();
      going = true;
      build();
      root.classList.add('hx-out');

      /* Drive the fade explicitly rather than leaning on a class-triggered CSS
         transition. Measured: the class landed and the rule matched
         (html.hx-out .hx-veil -> opacity 1, confirmed in the cascade) yet the
         computed value stayed flat at 0 across 15 consecutive frames — the
         style change and the transition start were not reliably separated.

         Web Animations sidesteps that entirely: the animation is explicit,
         starts when told, and its state can be read back. `forwards` holds the
         veil opaque so it cannot flash back before the browser unloads. */
      var go = function () {
        if (!going) return;
        window.location.href = url.href;
      };

      if (veil.animate) {
        var anim = veil.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: OUT, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
        );
        // Navigate when the veil is actually opaque, not on a parallel timer —
        // the two can drift on a loaded main thread and let the page show
        // through for a frame.
        if (anim.finished && anim.finished.then) {
          anim.finished.then(go).catch(go);
        } else {
          anim.onfinish = go;
          window.setTimeout(go, OUT + 60);
        }
      } else {
        // No WAAPI: skip the flourish rather than delay the visitor.
        veil.style.opacity = '1';
        go();
      }

      // If the navigation is blocked or refused, do not sit behind a veil.
      timer = window.setTimeout(clear, TIMEOUT);
    } catch (err) {
      clear();   // never trap the visitor behind a failed animation
    }
  }, false);

  /* Returning via back/forward restores the DOM exactly as it left — veil and
     all — so this has to run on every show, not just the first load. */
  window.addEventListener('pageshow', clear);
  window.addEventListener('popstate', clear);
})();
